import type { Permission } from '@pulse/shared';
import type { NavGroup, NavLeaf, NavNode } from '@pulse/ui';

function hasPermission(required: Permission | undefined, granted: Permission[]): boolean {
  return !required || granted.includes(required);
}

function filterGroupOrLeaf(
  children: (NavGroup | NavLeaf)[],
  granted: Permission[],
): (NavGroup | NavLeaf)[] {
  return children.reduce<(NavGroup | NavLeaf)[]>((acc, child) => {
    if (!hasPermission(child.permission, granted)) {
      return acc;
    }
    if (child.kind === 'page') {
      acc.push(child);
      return acc;
    }
    const leaves = child.children.filter((leaf) => hasPermission(leaf.permission, granted));
    if (leaves.length > 0) {
      acc.push({ ...child, children: leaves });
    }
    return acc;
  }, []);
}

// Pure, React-free — recursively prunes nodes a user lacks permission for. A Domain/Group is
// dropped if its own `permission` check fails, OR if it has zero children left after
// filtering (no bare empty group/domain headers). Follows the "filter in apps/web, keep
// @pulse/ui permission-agnostic" pattern used throughout AppLayout.tsx.
export function filterNavByPermissions(nodes: NavNode[], granted: Permission[]): NavNode[] {
  return nodes.reduce<NavNode[]>((acc, node) => {
    if (!hasPermission(node.permission, granted)) {
      return acc;
    }
    if (node.kind === 'page') {
      acc.push(node);
      return acc;
    }
    const children = filterGroupOrLeaf(node.children, granted);
    if (children.length > 0) {
      acc.push({ ...node, children });
    }
    return acc;
  }, []);
}
